import { Popover } from '@headlessui/react'
import { ChevronUpIcon, MenuIcon } from 'lucide-react'
import { Link } from '@remix-run/react'
import { AnimatePresence, motion } from 'framer-motion'
import { Button } from '~/components/ui/button.tsx'
import { NavLinks } from '~/components/NavLinks.tsx'
import React from 'react'
import type { User } from '@prisma/client'
import UserDropdown from '~/components/UserDropdown.tsx'
import cld from '~/services/cloudinary/cloudinaryInstance.tsx'
import { AdvancedImage } from '@cloudinary/react'

const logoPng = cld
	.image('pet-project/logo_transparent_ebyabh.png')
	.quality('auto')
	.format('auto')

type NavigationProps = {
	user?: User
}

function MobileNavLink({ children, ...props }) {
	return (
		<Popover.Button
			as={Link}
			to={props.href}
			className="block text-base leading-7 tracking-tight text-gray-700"
			{...props}
		>
			{children}
		</Popover.Button>
	)
}

export function Header({ user }: NavigationProps) {
	return (
		<header className="bg-lightGray">
			<nav>
				<div className="relative z-50 mx-auto flex max-w-7xl justify-between px-4 py-4 sm:px-6 lg:px-8">
					<div className="relative z-10 flex items-center gap-16">
						<Link to="/" aria-label="Home">
							<AdvancedImage
								cldImg={logoPng}
								className="h-10 w-auto"
								alt="logo"
							/>
						</Link>
						<Link
							to="/store"
							prefetch="intent"
							className="flex flex-row items-center text-xl font-light text-gray-900 transition hover:text-indigo-700 active:opacity-80"
						>
							<span className="font-bold text-gray-500">Pet</span>&nbsp;Canvas
							{/* <div className="mx-1" />*/}
							{/*<small className="relative top-[2px] text-sm font-bold text-violet-300">*/}
							{/*	.AI*/}
							{/*</small>*/}
						</Link>
						<div className="hidden lg:flex lg:gap-10">
							<NavLinks />
						</div>
					</div>
					<div className="flex items-center gap-10">
						<Popover className="lg:hidden">
							{({ open }) => (
								<>
									<Popover.Button
										className="relative z-10 -m-2 inline-flex items-center rounded-lg stroke-gray-900 p-2 hover:bg-gray-200/50 hover:stroke-gray-600 active:stroke-gray-900 [&:not(:focus-visible)]:focus:outline-none"
										aria-label="Toggle site navigation"
									>
										{({ open }) =>
											open ? (
												<ChevronUpIcon className="h-6 w-6" />
											) : (
												<MenuIcon className="h-6 w-6" />
											)
										}
									</Popover.Button>
									<AnimatePresence initial={false}>
										{open && (
											<>
												<Popover.Overlay
													static
													as={motion.div}
													initial={{ opacity: 0 }}
													animate={{ opacity: 1 }}
													exit={{ opacity: 0 }}
													className="fixed inset-0 z-0 bg-harvestGold/70 backdrop-blur"
												/>
												<Popover.Panel
													static
													as={motion.div}
													initial={{ opacity: 0, y: -32 }}
													animate={{ opacity: 1, y: 0 }}
													exit={{
														opacity: 0,
														y: -32,
														transition: { duration: 0.2 },
													}}
													className="absolute inset-x-0 top-0 z-0 origin-top rounded-b-2xl bg-gray-50 px-6 pb-6 pt-32 shadow-2xl shadow-gray-900/20"
												>
													<div className="space-y-4">
														<MobileNavLink href="/#features">
															How It Works
														</MobileNavLink>
														<MobileNavLink href="/#reviews">
															Reviews
														</MobileNavLink>
														<MobileNavLink href="/store">Store</MobileNavLink>
														<MobileNavLink href="/#faqs">FAQs</MobileNavLink>
													</div>
													<div className="mt-8 flex flex-col gap-4">
														<Button asChild variant="default" size="sm">
															<Link to="/store">Shop</Link>
														</Button>
														{user ? (
															<UserDropdown />
														) : (
															<Button asChild variant="default" size="sm">
																<Link to="/login">Log In</Link>
															</Button>
														)}
													</div>
												</Popover.Panel>
											</>
										)}
									</AnimatePresence>
								</>
							)}
						</Popover>
						<Button variant="default" size="sm" className="hidden lg:block">
							<Link to="/store">Shop</Link>
						</Button>
						{user ? (
							<UserDropdown />
						) : (
							<Button
								// asChild
								variant="default"
								size="sm"
								className="hidden lg:block"
							>
								<Link to="/login">Log In</Link>
							</Button>
						)}
					</div>
				</div>
			</nav>
		</header>
	)
}
